<script>
  // Chrome-style tab bar — one tab per opened key. Click to switch, X to close.
  // Plus an "+" button that opens a dropdown listing keys that DON'T have
  // a tab open yet, click one to open it as a new tab.
  import { tabs, getActiveTabId, setActiveTabId, closeTab, openTabForKey } from "../stores/app.svelte.js";

  let { keys } = $props();

  const activeId = $derived(getActiveTabId());
  // Reactive computation: which keys still need a tab?
  const openableKeys = $derived(
    (keys || []).filter((k) => !tabs.some((t) => t.keyId === k.id))
  );

  let showAddDropdown = $state(false);

  // Per-tab status dot color based on the job state inside that tab.
  // Lets the user scan the strip and spot a running / done / failed job
  // without switching to that tab.
  function tabStatusClass(tab) {
    if (tab.view === "tracking" || tab.view === "submitting") return "running";
    if (tab.view === "succeeded") return "done";
    if (tab.view === "failed")    return "error";
    return "idle";
  }
</script>

<svelte:window onclick={() => (showAddDropdown = false)} />

<nav class="tab-bar">
  <!-- .tab-bar-inner is a flex row: tab-list (scrollable) + add-wrap
       (NOT inside the scrollable area so its dropdown can overflow). -->
  <div class="tab-bar-inner">
    <div class="tab-list">
      {#each tabs as tab (tab.keyId)}
        <button class="tab" class:active={tab.keyId === activeId}
                onclick={() => setActiveTabId(tab.keyId)}>
          <span class="tab-dot {tabStatusClass(tab)}"></span>
          <span class="tab-label">{tab.label}</span>
          {#if tabs.length > 1}
            <span class="tab-close" role="button" tabindex="0"
                  onclick={(e) => { e.stopPropagation(); closeTab(tab.keyId); }}
                  onkeydown={(e) => { if (e.key === "Enter") { e.stopPropagation(); closeTab(tab.keyId); }}}>×</span>
          {/if}
        </button>
      {/each}
    </div>

    <!-- Add-wrap sits OUTSIDE .tab-list so its absolutely-positioned
         dropdown isn't clipped by the tab-list's overflow-x: auto.
         stopPropagation keeps the window onclick from immediately closing
         the dropdown after the +button click bubbles up. -->
    <div class="add-wrap" role="presentation" onclick={(e) => e.stopPropagation()}>
      <button class="add-tab"
              onclick={() => (showAddDropdown = !showAddDropdown)}
              title={openableKeys.length === 0 ? "Tất cả keys đã có tab" : "Thêm tab"}>
        +
      </button>
      {#if showAddDropdown}
        <div class="add-dropdown">
          {#if openableKeys.length > 0}
            <div class="add-dropdown-header">Mở tab cho key:</div>
            {#each openableKeys as k}
              <button class="add-dropdown-item"
                      onclick={() => { openTabForKey(k); showAddDropdown = false; }}>
                <span class="key-label">{k.label}</span>
                <span class="key-meta">${k.balance?.toFixed(2) ?? "—"}</span>
              </button>
            {/each}
          {:else}
            <div class="add-dropdown-empty">
              Tất cả keys đã có tab đang mở.<br />
              Đóng 1 tab để mở lại, hoặc thêm key mới qua <strong>🔑 Keys</strong> ở header.
            </div>
          {/if}
        </div>
      {/if}
    </div>
  </div>
</nav>

<style>
  .tab-bar {
    background: var(--bg-elevated);
    border-bottom: 1px solid var(--border);
  }

  /* Outer wrap = centered container + flex row.
     The dropdown sits in .add-wrap which is a sibling of .tab-list, so
     the tab-list's overflow-x: auto can't clip it vertically. */
  .tab-bar-inner {
    max-width: min(1400px, 96vw);
    margin: 0 auto;
    padding: 0 20px;
    display: flex;
    gap: 2px;
    align-items: flex-end;
  }

  .tab-list {
    flex: 1;
    display: flex;
    gap: 2px;
    overflow-x: auto;
    align-items: flex-end;
    min-width: 0;
  }

  .tab {
    display: inline-flex;
    align-items: center;
    gap: 8px;
    padding: 8px 12px 8px 14px;
    background: var(--panel-2);
    border: 1px solid var(--border);
    border-bottom: none;
    border-top-left-radius: 8px;
    border-top-right-radius: 8px;
    color: var(--muted-strong);
    font-size: 0.82rem;
    cursor: pointer;
    transition: background 0.15s, color 0.15s;
    margin-bottom: -1px;
    white-space: nowrap;
    min-width: 0;
  }

  .tab:hover { background: var(--panel); color: var(--text); }

  .tab.active {
    background: var(--panel);
    color: var(--text-strong);
    border-color: var(--border-strong);
    z-index: 2;
  }

  .tab-dot {
    width: 8px;
    height: 8px;
    border-radius: 50%;
    background: var(--muted);
    flex-shrink: 0;
  }
  .tab-dot.running { background: var(--accent); animation: pulse 1.5s ease-in-out infinite; }
  .tab-dot.done    { background: #4cd197; }
  .tab-dot.error   { background: #ff8585; }
  .tab-dot.idle    { background: var(--muted); }

  @keyframes pulse {
    0%, 100% { opacity: 1; }
    50%      { opacity: 0.4; }
  }

  .tab-label {
    max-width: 180px;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  .tab-close {
    width: 18px;
    height: 18px;
    border-radius: 50%;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    font-size: 1rem;
    color: var(--muted);
    transition: background 0.15s, color 0.15s;
    line-height: 1;
  }
  .tab-close:hover {
    background: rgba(248, 113, 113, 0.2);
    color: var(--danger);
  }

  .add-wrap { position: relative; }

  .add-tab {
    width: 32px;
    height: 32px;
    border-radius: 6px;
    background: transparent;
    color: var(--muted);
    font-size: 1.2rem;
    transition: background 0.15s;
    margin-bottom: 4px;
  }
  .add-tab:not(:disabled):hover { background: var(--panel); color: var(--text-strong); }
  .add-tab:disabled              { opacity: 0.3; cursor: not-allowed; }

  .add-dropdown {
    position: absolute;
    top: calc(100% + 4px);
    left: 0;
    min-width: 240px;
    background: var(--panel);
    border: 1px solid var(--border);
    border-radius: 8px;
    box-shadow: var(--shadow-md);
    z-index: 100;
    padding: 4px;
  }

  .add-dropdown-header {
    padding: 8px 10px 4px;
    font-size: 0.7rem;
    color: var(--muted);
    text-transform: uppercase;
    letter-spacing: 0.04em;
  }

  .add-dropdown-item {
    width: 100%;
    text-align: left;
    padding: 8px 10px;
    border-radius: 4px;
    display: flex;
    justify-content: space-between;
    gap: 12px;
    font-size: 0.82rem;
    color: var(--text);
  }

  .add-dropdown-item:hover { background: var(--panel-2); }

  .add-dropdown-empty {
    padding: 12px 14px;
    font-size: 0.78rem;
    color: var(--muted);
    line-height: 1.5;
    text-align: center;
  }

  .key-label { font-weight: 500; }
  .key-meta  { color: var(--muted); font-variant-numeric: tabular-nums; }
</style>
